import { AlertTriangle, BookOpen, ChevronDown, Clock, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { useState } from 'react';

import type { AnaliseIaResponse, FonteDoGuideResponse, ItemDaAnaliseResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { normalizarAnalise, temTextoDeIa } from '@/features/analises/components/normalizarAnalise';
import { EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Button } from '@/shared/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible';

interface Props {
  analise: AnaliseIaResponse | undefined;
  carregando: boolean;
  gerando: boolean;
  erro: unknown;
  aoGerar: () => void;
  /** Some no `Ctrl+P` — ver o comentário do componente. */
  ocultarNaImpressao?: boolean;
  /**
   * Esconde o botão "Gerar análise"/"Gerar de novo" — usado pelo laudo do
   * exame, onde a geração é automática (ver `LaudoDoExamePage`). O grupo da
   * Análise Comparativa não passa isto: lá continua manual.
   */
  ocultarBotaoDeGerar?: boolean;
}

/**
 * Painel de análise por IA — o mesmo para grupo e para laudo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ A SEPARAÇÃO ENTRE FATO E SUGESTÃO É O PONTO DESTE COMPONENTE.
 *
 *  `achados` e `padroes` vêm dos números que o servidor calculou. `hipoteses` e
 *  `investigar` são suposição da IA — e vivem num bloco com borda, ícone e
 *  cabeçalho próprios, adjacente ao conteúdo, NÃO num rodapé.
 *
 *  O aviso não é dispensável de propósito: num contexto clínico, um parágrafo
 *  corrido de IA é lido inteiro como achado, e um disclaimer que some ao ser
 *  fechado não segura nada. A separação já vem pronta do contrato (campos
 *  diferentes no JSON), então a tela não teria como misturá-los nem querendo.
 *
 *  ⚠️ **A CITAÇÃO DO GUIDE NÃO PODE VIRAR SELO DE VALIDAÇÃO.** A IA passou a
 *  responder com base no Guide Hormonalle e a dizer de onde tirou cada coisa, e
 *  o instinto seria pôr um badge "Cap. 5.2 · p.51" ao lado da frase. Isso
 *  desmontaria o componente por dentro: número de página lê-se universalmente
 *  como verificação, e o veterinário resolveria a contradição com o aviso âmbar
 *  a favor do badge. Por isso a fonte aparece em registro SUBORDINADO — texto
 *  pequeno, `muted`, com o verbo dizendo o que ela é ("critério lido em") — e
 *  nunca com fundo, borda ou cor própria.
 *
 *  ⚠️ **Página sem trecho não se exibe.** Os PDFs do Guide não estão no
 *  aplicativo nem com o clínico: do assento dele, uma página real e uma
 *  inventada são indistinguíveis. A citação abre e mostra o parágrafo que o
 *  modelo leu — é isso, e não o número, que torna a coisa conferível.
 *
 *  ⚠️ **O aviso âmbar deixou de ser condicional a haver hipótese.** Se há
 *  qualquer texto de IA na tela, o aviso está na tela: senão ele desaparece
 *  justamente quando o modelo produziu menos, que é quando ele foi menos
 *  confiável.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function PainelDeAnaliseIa({
  analise,
  carregando,
  gerando,
  erro,
  aoGerar,
  ocultarNaImpressao,
  ocultarBotaoDeGerar,
}: Props) {
  const { pode } = usePermissoes();
  const podeGerar = pode('analises_ia.criar');

  // ⚠️ ANTES dos guards de tamanho, não dentro do `.map()`: análise antiga vem
  // do banco como `string[]` e passaria pelo `length > 0` para quebrar por
  // dentro. Ver `normalizarAnalise.ts`.
  const conteudo = normalizarAnalise(analise?.conteudo);

  // 503 não é falha nossa: é ambiente sem IA configurada. Mostrar "algo deu
  // errado" mandaria o usuário procurar defeito onde não há.
  if (erro instanceof ApiError && erro.status === 503) {
    return (
      <div className={ocultarNaImpressao ? 'print:hidden' : undefined}>
        <EstadoVazio
          icone={Sparkles}
          titulo="Análise por IA não está ativa"
          descricao="Este ambiente não tem a integração de IA configurada. Fale com o administrador da plataforma."
        />
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${ocultarNaImpressao ? 'print:hidden' : ''}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">
          {analise?.existe ? (
            <>
              Gerada em {formatarData(analise.geradaEm)}
              {analise.geradoPorNome ? ` por ${analise.geradoPorNome}` : ''}
            </>
          ) : gerando ? (
            'Gerando análise...'
          ) : (
            'Nenhuma análise gerada ainda.'
          )}
        </div>

        {podeGerar && !ocultarBotaoDeGerar && (
          <Button variant="outline" onClick={aoGerar} disabled={gerando || carregando} className="gap-2">
            {gerando ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : analise?.existe ? (
              <RefreshCw className="w-4 h-4" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {gerando ? 'Consultando o Guide Hormonalle...' : analise?.existe ? 'Gerar de novo' : 'Gerar análise'}
          </Button>
        )}
      </div>

      {/* ⚠️ Sem este aviso, uma análise de março continua parecendo atual em
          agosto — e quem lê não tem como saber que ela não viu os exames
          novos. */}
      {analise?.desatualizada && (
        <Aviso>Há exame mais recente que esta análise. Gere de novo para considerar os dados novos.</Aviso>
      )}

      {/* O irmão do de cima, no eixo do CONHECIMENTO em vez do eixo do dado:
          depois de uma reindexação, as páginas citadas podem não ser as
          mesmas. O trecho gravado junto continua valendo; o número, talvez não. */}
      {analise?.existe && analise.baseDesatualizada && (
        <Aviso>
          Esta análise foi gerada com outra versão do Guide Hormonalle. Os trechos citados são os que a
          IA leu, mas as páginas podem ter mudado.
        </Aviso>
      )}

      {carregando || (gerando && !analise?.existe) ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
          {gerando ? 'Gerando análise...' : 'Carregando...'}
        </p>
      ) : !analise?.existe || !conteudo ? (
        <EstadoVazio
          icone={Sparkles}
          titulo="Sem análise ainda"
          descricao={
            podeGerar
              ? 'Gere uma leitura do que estes exames mostram, com base no Guide Hormonalle.'
              : 'Ninguém gerou uma análise para este conteúdo, e seu cargo não permite gerar.'
          }
        />
      ) : (
        <div className="space-y-5">
          {/* ⚠️ Estado PRÓPRIO, distinto de "sem análise ainda". A análise foi
              gerada e paga; o que não houve foi cobertura do Guide. Sem esta
              distinção o veterinário clica de novo e recebe o mesmo resultado. */}
          {!conteudo.guideCobriu && temTextoDeIa(conteudo) && (
            <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm">
              <BookOpen className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
              <p className="text-muted-foreground">
                O Guide Hormonalle não cobre este exame ou esta espécie. A leitura abaixo saiu dos
                números e do conhecimento geral do modelo — nada nela tem respaldo do Guide Hormonalle.
              </p>
            </div>
          )}

          {/* --- Derivado dos dados ------------------------------------------ */}
          {conteudo.achados.length > 0 && (
            <Secao titulo="Achados" legenda="derivados dos números deste laudo">
              <Lista itens={conteudo.achados} marcador="text-brand" />
            </Secao>
          )}

          {conteudo.padroes.length > 0 && (
            <Secao titulo="Padrões" legenda="derivados dos números deste laudo">
              <Lista itens={conteudo.padroes} marcador="text-brand" />
            </Secao>
          )}

          {/* --- Sugestão da IA ---------------------------------------------- */}
          {temTextoDeIa(conteudo) && (
            <div className="rounded-lg border-2 border-amber-500/40 bg-amber-500/5 overflow-hidden">
              {/* O aviso fica COLADO no conteúdo que ele qualifica, e não some. */}
              <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/10 border-b border-amber-500/30">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-500 shrink-0" />
                <p className="text-sm font-medium">
                  Sugestões da IA — não são diagnóstico nem verdade absoluta
                </p>
              </div>

              <div className="p-4 space-y-4">
                <p className="text-xs text-muted-foreground">
                  O que vem abaixo é interpretação de um modelo de linguagem sobre os números acima.
                  A referência do Guide Hormonalle diz de onde veio o critério — não que ele se aplique a este
                  paciente. Confirme com seu próprio julgamento clínico antes de usar.
                </p>

                {conteudo.hipoteses.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-1.5">Hipóteses a considerar</h4>
                    <Lista itens={conteudo.hipoteses} marcador="text-amber-600 dark:text-amber-500" />
                  </div>
                )}

                {conteudo.investigar.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-1.5">O que poderia ser investigado</h4>
                    <Lista itens={conteudo.investigar} marcador="text-amber-600 dark:text-amber-500" />
                  </div>
                )}

                {conteudo.hipoteses.length === 0 && conteudo.investigar.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    A IA não levantou nenhuma hipótese a partir destes dados.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* --- O que o dado não sustenta ------------------------------------ */}
          {conteudo.limitacoes.length > 0 && (
            <Secao titulo="Limitações destes dados">
              <Lista itens={conteudo.limitacoes} marcador="" esmaecido />
            </Secao>
          )}

          {/* ⚠️ Bloco consolidado no fim, e não quinze referências espalhadas
              pelos bullets: caçadas frase a frase elas viram papel de parede em
              duas semanas, e a citação deixa de significar qualquer coisa. */}
          {analise.fontes.length > 0 && (
            <Secao titulo="Trechos do Guide Hormonalle usados nesta leitura">
              <div className="space-y-2">
                {analise.fontes.map((fonte, i) => (
                  <TrechoDoGuide key={`${fonte.pagina}-${i}`} fonte={fonte} />
                ))}
              </div>
            </Secao>
          )}

          <p className="text-xs text-muted-foreground pt-2 border-t border-border">
            Gerada por {analise.modelo ?? 'modelo não registrado'}
            {analise.edicaoDoGuide ? ` · base: ${analise.edicaoDoGuide}` : ' · sem base do Guide Hormonalle'}
            {analise.corpusVersao ? ` (corpus ${analise.corpusVersao})` : ''}.
          </p>
        </div>
      )}
    </div>
  );
}

/** Uma lista de afirmações, cada uma com a procedência logo abaixo. */
function Lista({
  itens,
  marcador,
  esmaecido,
}: {
  itens: ItemDaAnaliseResponse[];
  marcador: string;
  esmaecido?: boolean;
}) {
  return (
    <ul className="space-y-2">
      {itens.map((item, i) => (
        // ⚠️ Índice, e não o texto: com objetos a chave viraria `[object Object]`
        // repetido, e duas frases iguais quebrariam a reconciliação do React.
        <li key={i} className={`text-sm ${esmaecido ? 'text-muted-foreground' : ''}`}>
          <div className="flex gap-2">
            <span className={`shrink-0 ${marcador}`}>•</span>
            <span>{item.texto}</span>
          </div>

          {item.fontes.length > 0 && (
            <div className="pl-4 mt-1 space-y-1">
              {/* Uma fonte visível; o resto atrás do expandir. Três referências
                  num item leem-se como grau de certeza que ninguém quis emitir. */}
              <TrechoDoGuide fonte={item.fontes[0]!} extras={item.fontes.length - 1} />
            </div>
          )}

          {item.semRespaldo && (
            // ⚠️ Marcado, nunca escondido. Sumir com o item apagaria o aviso da
            // tela justamente quando o modelo foi menos confiável.
            <p className="pl-4 mt-1 text-xs text-muted-foreground italic">sem respaldo no Guide Hormonalle</p>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * A citação, em registro subordinado e sempre acompanhada do texto.
 *
 * `Collapsible` e não `Tooltip`: o `TooltipProvider` só existe na
 * `AnaliseComparativaPage` — dentro do laudo, e em todos os testes de
 * componente, um Radix Tooltip quebraria em runtime.
 */
function TrechoDoGuide({ fonte, extras = 0 }: { fonte: FonteDoGuideResponse; extras?: number }) {
  const [aberto, setAberto] = useState(false);

  return (
    <Collapsible open={aberto} onOpenChange={setAberto}>
      <CollapsibleTrigger className="text-xs text-muted-foreground hover:text-foreground text-left inline-flex items-start gap-1">
        <ChevronDown
          className={`w-3 h-3 mt-0.5 shrink-0 transition-transform ${aberto ? 'rotate-180' : ''}`}
        />
        <span>
          critério lido em: {fonte.capituloNome} · p. {fonte.pagina}
          {extras > 0 ? ` +${extras}` : ''}
        </span>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="mt-1.5 ml-4 border-l-2 border-border pl-3 py-1 space-y-1">
          <p className="text-xs font-medium text-muted-foreground">
            {fonte.secao} — {fonte.rotuloDaNatureza}
          </p>
          <p className="text-xs leading-relaxed">{fonte.trecho}</p>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function Aviso({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
      <Clock className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-500" />
      <p>{children}</p>
    </div>
  );
}

function Secao({
  titulo,
  legenda,
  children,
}: {
  titulo: string;
  legenda?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h4 className="text-sm font-semibold text-foreground mb-2">
        {titulo}
        {legenda && <span className="ml-2 text-xs font-normal text-muted-foreground">{legenda}</span>}
      </h4>
      {children}
    </div>
  );
}

/** `12/03/2026 às 14:32`. `null` quando nunca gerada. */
function formatarData(iso: string | null): string {
  if (!iso) return '—';

  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
